import React, { useEffect, useRef, useState, useCallback } from 'react'
import EditorJS, { OutputData, API } from '@editorjs/editorjs'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import Paragraph from '@editorjs/paragraph'
import Quote from '@editorjs/quote'
// @ts-ignore
import Marker from '@editorjs/marker'
// @ts-ignore
import Checklist from '@editorjs/checklist'
import Delimiter from '@editorjs/delimiter'
import Table from '@editorjs/table'
import Image from '@editorjs/image'
import { TwoColumnLayoutTool } from './custom-tools/TwoColumnLayoutTool'
import { ImageTextTool } from './custom-tools/ImageTextTool'
import { uploaderService } from '../../shared/service'
import { resolvePathFile } from '../../../core/until'
import { FloatButton } from 'antd'
import { ClearOutlined } from '@ant-design/icons'
import { translate } from '../../../core'

interface ContentEditorProps {
  value?: OutputData
  onChange?: (data: OutputData) => void
  placeholder?: string
  readOnly?: boolean
  className?: string
  style?: React.CSSProperties
  showCustomTools?: boolean
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing...',
  readOnly = false,
  className = '',
  style = {},
  showCustomTools = true
}) => {
  const editorRef = useRef<EditorJS>()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showRenderedHtml, setShowRenderedHtml] = useState(false)
  const [renderedHtml, setRenderedHtml] = useState<string | null>(null)
  const [lastValue, setLastValue] = useState<any>(null)

  // Initialize Editor.js with custom tools
  const initEditor = useCallback(async () => {
    if (!containerRef.current) return

    try {
      setIsLoading(true)
      
      const tools: any = {
        header: {
          class: Header,
          config: {
            placeholder: 'Enter a header',
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 2
          }
        },
        list: {
          class: List,
          inlineToolbar: true,
          config: {
            defaultStyle: 'unordered'
          }
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true,
          config: {
            placeholder: 'Enter a paragraph'
          }
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: 'Enter a quote',
            captionPlaceholder: 'Quote\'s author'
          }
        },
        marker: {
          class: Marker,
          shortcut: 'CMD+SHIFT+M'
        },
        checklist: {
          class: Checklist,
          inlineToolbar: true
        },
        delimiter: Delimiter,
        table: {
          class: Table,
          inlineToolbar: true,
          config: {
            rows: 2,
            cols: 3
          }
        },
        image: {
          class: Image,
          config: {
            uploader: {
              uploadByFile: async (file: File) => {
                const [result] = await uploaderService.uploadImages([file as any])
                return {
                  success: 1,
                  file: {
                    url: resolvePathFile(result.filename)
                  }
                }
              },
              uploadByUrl: async () => {
                return {
                  filename: 'test.jpg',
                  url: 'https://example.com/image.jpg'
                }
              },
            }
          }
        }
      }

      // Add custom tools if enabled
      if (showCustomTools) {
        tools.twoColumnLayout = {
          class: TwoColumnLayoutTool,
          config: {
            placeholder: 'Enter your text content here...',
            defaultLayout: 'image-left'
          }
        }
        
        tools.imageText = {
          class: ImageTextTool,
          config: {
            placeholder: 'Enter your text content here...'
          }
        }
      }

      const editor = new EditorJS({
        holder: containerRef.current,
        data: value || {
          blocks: []
        },
        tools,
        placeholder,
        readOnly,
        onChange: async (api: API) => {
          try {
            const data = await api.saver.save()
            console.log('ContentEditor onChange:', data)
            setLastValue(data)
            onChange?.(data)
          } catch (error) {
            console.error('Error saving editor data:', error)
          }
        },
        onReady: () => {
          setIsReady(true)
          setIsLoading(false)
          
          // Add keyboard shortcuts for clearing formatting
          if (containerRef.current) {
            const handleKeyDown = (e: KeyboardEvent) => {
              // Ctrl/Cmd + Shift + X to clear formatting
              if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'X') {
                e.preventDefault()
                clearFormatting()
              }
            }
            
            containerRef.current.addEventListener('keydown', handleKeyDown)
            
            // Store the handler for cleanup
            ;(containerRef.current as any)._formattingHandler = handleKeyDown
          }
        },
        autofocus: true,
        minHeight: 200,
        // Additional configuration to help with formatting issues
        logLevel: 'WARN' as any
      })

      editorRef.current = editor
    } catch (error) {
      console.error('Error initializing Editor.js:', error)
      setIsLoading(false)
    }
  }, [placeholder, readOnly, onChange, showCustomTools])

  // Clear formatting function
  const clearFormatting = useCallback(() => {
    if (editorRef.current && isReady) {
      try {
        // Get the current selection
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          
          // Remove all formatting by replacing with plain text
          const textContent = range.toString()
          if (textContent) {
            // Create a new text node without formatting
            const textNode = document.createTextNode(textContent)
            range.deleteContents()
            range.insertNode(textNode)
            
            // Update the selection to the new text
            selection.removeAllRanges()
            const newRange = document.createRange()
            newRange.selectNode(textNode)
            selection.addRange(newRange)
            
            // Trigger Editor.js to update its state
            if (editorRef.current) {
              editorRef.current.saver.save().then((data) => {
                console.log('Formatting cleared, updated data:', data)
              }).catch((error) => {
                console.error('Error saving after clearing formatting:', error)
              })
            }
          }
        }
      } catch (error) {
        console.error('Error clearing formatting:', error)
      }
    }
  }, [isReady])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy?.()
      }
      
      // Clean up event listener
      if (containerRef.current && (containerRef.current as any)._formattingHandler) {
        containerRef.current.removeEventListener('keydown', (containerRef.current as any)._formattingHandler)
      }
    }
  }, [])

  // Initialize editor when component mounts
  useEffect(() => {
    initEditor()
  }, [initEditor])

  // Handle external value changes (like loading existing content)
  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(lastValue)) {
      // This is an external update (like loading existing content)
      // We need to update the editor content
      if (editorRef.current && isReady) {
        // Destroy and recreate with new data
        editorRef.current.destroy?.()
        initEditor()
      }
    }
  }, [value, lastValue, isReady, initEditor])



  // Handle content updates
  const handleSave = useCallback(async () => {
    if (editorRef.current && isReady) {
      try {
        const data = await editorRef.current.save()
        onChange?.(data)
        return data
      } catch (error) {
        console.error('Error saving editor data:', error)
        return null
      }
    }
  }, [isReady, onChange])

  // Clear editor content
  const handleClear = useCallback(() => {
    if (editorRef.current && isReady) {
      editorRef.current.clear()
    }
  }, [isReady])

  // Render content as HTML
  const handleRender = useCallback(async () => {
    if (editorRef.current && isReady) {
      try {
        // Get current data
        const data = await editorRef.current.save();
        
        // Custom render method for custom tools
        let html = '';
        
        if (data.blocks && data.blocks.length > 0) {
          html = data.blocks.map(block => {
            switch (block.type) {
              case 'header':
                return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
              
              case 'paragraph':
                return `<p>${block.data.text}</p>`;
              
              case 'list':
                const listType = block.data.style === 'ordered' ? 'ol' : 'ul';
                const listItems = block.data.items.map((item: any) => `<li>${item}</li>`).join('');
                return `<${listType}>${listItems}</${listType}>`;
              
              case 'quote':
                return `<blockquote><p>${block.data.text}</p><cite>${block.data.caption || ''}</cite></blockquote>`;
              
              case 'twoColumnLayout':
                // Render the two-column layout as HTML
                const imageWidth = block.data.imageWidth || 40;
                const textWidth = 100 - imageWidth;
                const layout = block.data.layout || 'image-left';
                const textAlign = block.data.textAlignment || 'left';
                
                if (layout === 'image-left') {
                  return `
                    <div style="display: flex; gap: 16px; align-items: start; margin: 16px 0; flex-direction: row;">
                      <div style="flex: 0 0 ${imageWidth}%; max-width: ${imageWidth}%; min-width: 200px;">
                        ${block.data.imageUrl ? 
                          `<img src="${block.data.imageUrl}" alt="${block.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">` : 
                          '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Image</div>'
                        }
                      </div>
                      <div style="flex: 1; min-width: 250px; text-align: ${textAlign};">
                        <div style="padding: 20px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
                          ${block.data.textContent ? 
                            `<div style="margin: 0; line-height: 1.7; color: #333; font-size: 16px;">${block.data.textContent}</div>` : 
                            '<div style="margin: 0; color: #999; font-style: italic;">No content</div>'
                          }
                        </div>
                      </div>
                    </div>
                  `;
                } else {
                  return `
                    <div style="display: flex; gap: 16px; align-items: start; margin: 16px 0; flex-direction: row;">
                      <div style="flex: 1; min-width: 250px; text-align: ${textAlign};">
                        <div style="padding: 20px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
                          ${block.data.textContent ? 
                            `<div style="margin: 0; line-height: 1.7; color: #333; font-size: 16px;">${block.data.textContent}</div>` : 
                            '<div style="margin: 0; color: #999; font-style: italic;">No content</div>'
                          }
                        </div>
                      </div>
                      <div style="flex: 0 0 ${imageWidth}%; max-width: ${imageWidth}%; min-width: 200px;">
                        ${block.data.imageUrl ? 
                          `<img src="${block.data.imageUrl}" alt="${block.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">` : 
                          '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Image</div>'
                        }
                      </div>
                    </div>
                  `;
                }
              
              case 'imageText':
                // Render the image with text overlay as HTML
                const height = block.data.height || 300;
                const textPosition = block.data.textPosition || 'center';
                const textColor = block.data.textColor || '#ffffff';
                const backgroundColor = block.data.backgroundColor || 'rgba(0, 0, 0, 0.3)';
                const overlayOpacity = block.data.overlayOpacity || 0.5;
                const textSize = block.data.textSize || 'medium';
                
                const getTextSize = (size: string) => {
                  const sizes = { small: '14px', medium: '18px', large: '24px', xlarge: '32px' };
                  return sizes[size as keyof typeof sizes] || sizes.medium;
                };
                
                const getTextPosition = (position: string) => {
                  const positions: Record<string, string> = {
                    'top-left': 'top: 0; left: 0; text-align: left;',
                    'top-center': 'top: 0; left: 50%; transform: translateX(-50%); text-align: center;',
                    'top-right': 'top: 0; right: 0; text-align: right;',
                    'center-left': 'top: 50%; left: 0; transform: translateY(-50%); text-align: left;',
                    'center': 'top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center;',
                    'center-right': 'top: 50%; right: 0; transform: translateY(-50%); text-align: right;',
                    'bottom-left': 'bottom: 0; left: 0; text-align: left;',
                    'bottom-center': 'bottom: 0; left: 50%; transform: translateX(-50%); text-align: center;',
                    'bottom-right': 'bottom: 0; right: 0; text-align: right;'
                  };
                  return positions[position] || positions.center;
                };
                
                return `
                  <div style="position: relative; width: 100%; height: ${height}px; border-radius: 8px; overflow: hidden; margin: 16px 0;">
                    ${block.data.imageUrl ? 
                      `<img src="${block.data.imageUrl}" alt="${block.data.imageAlt || 'Image'}" style="width: 100%; height: 100%; object-fit: cover;">` : 
                      '<div style="width: 100%; height: 100%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #999;">No Image</div>'
                    }
                    ${block.data.imageUrl ? 
                      `<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: ${backgroundColor}; opacity: ${overlayOpacity};"></div>` : 
                      ''
                    }
                    ${block.data.textContent ? 
                      `<div style="position: absolute; color: ${textColor}; font-size: ${getTextSize(textSize)}; font-weight: 600; padding: 20px; z-index: 2; max-width: calc(100% - 40px); word-wrap: break-word; box-sizing: border-box; ${getTextPosition(textPosition)}">${block.data.textContent}</div>` : 
                      ''
                    }
                  </div>
                `;
              
              default:
                // For other block types, try to render them as best we can
                if (block.data && block.data.text) {
                  return `<p>${block.data.text}</p>`;
                }
                return `<div>Unsupported block type: ${block.type}</div>`;
            }
          }).join('');
        }
        
        console.log('Generated HTML:', html);
        setRenderedHtml(html);
        setShowRenderedHtml(true);
        return html;
      } catch (error) {
        console.error('Error rendering editor:', error);
        return null;
      }
    }
  }, [isReady])

  return (
    <div className={`content-editor-container ${className}`} style={{ position: 'relative', ...style }}>
      {isLoading && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666' 
        }}>
          Loading editor...
        </div>
      )}

      {/* Floating Action Buttons */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '10px'
      }}>
        <FloatButton
          style={{
            left: 8,
            top: 8,
            zIndex: 9999,
            position: 'absolute',
          }}
          icon={<ClearOutlined/>}
          onClick={handleClear}
          type="default"
          tooltip={translate("clear_all_content")}
        />
      </div>
      
      <div 
        ref={containerRef} 
        style={{ 
          minHeight: '200px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '10px'
        }}
      />
      
      {/* {isReady && !readOnly && (
        <div style={{ 
          marginTop: '10px', 
          display: 'flex', 
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Save Content
          </button>
          
          <button 
            onClick={handleClear}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ff4d4f',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
          
          <button 
            onClick={handleRender}
            style={{
              padding: '8px 16px',
              backgroundColor: '#52c41a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Render HTML
          </button>

          {showCustomTools && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#722ed1',
              color: 'white',
              borderRadius: '4px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span>✨</span>
              Custom Tools Enabled
            </div>
          )}
        </div>
      )}

      {showRenderedHtml && renderedHtml && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          border: '2px solid #1890ff',
          borderRadius: '8px',
          backgroundColor: '#f0f8ff'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: 0, color: '#1890ff' }}>Rendered HTML Output</h4>
            <button
              onClick={() => setShowRenderedHtml(false)}
              style={{
                padding: '4px 8px',
                backgroundColor: '#ff4d4f',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Close
            </button>
          </div>
          <div 
            style={{
              backgroundColor: 'white',
              padding: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '400px',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        </div>
      )} */}
    </div>
  )
}

export default ContentEditor

